<template>
  <Teleport to="body">
    <div
      class="fixed top-20 right-4 z-[9999] flex flex-col gap-3 pointer-events-none"
      role="region"
      aria-label="Notificaciones"
      aria-live="polite"
    >
      <TransitionGroup name="toast">
        <div
          v-for="toast in toasts"
          :key="toast.id"
          :class="[
            'pointer-events-auto w-96 max-w-[calc(100vw-2rem)] rounded-2xl shadow-xl shadow-slate-200/50 border backdrop-blur-lg transition-all duration-300',
            toast.colorClasses.bg,
            toast.colorClasses.border
          ]"
          role="alert"
          :aria-label="`${toast.type} notification: ${toast.message}`"
        >
          <!-- Toast Content -->
          <div class="p-4">
            <div class="flex items-start gap-3">
              <!-- Icon -->
              <div class="flex-shrink-0">
                <component
                  :is="getIconComponent(toast.icon)"
                  :class="['w-6 h-6', toast.colorClasses.icon]"
                  aria-hidden="true"
                />
              </div>

              <!-- Message -->
              <div class="flex-1 min-w-0">
                <p :class="['text-sm font-medium', toast.colorClasses.text]">
                  {{ toast.message }}
                </p>
              </div>

              <!-- Close Button -->
              <button
                type="button"
                @click="removeToast(toast.id)"
                class="flex-shrink-0 inline-flex rounded-xl p-1.5 hover:bg-white/50 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200"
                :class="toast.colorClasses.icon"
                :aria-label="`Cerrar notificación: ${toast.message}`"
              >
                <XMarkIcon class="w-5 h-5" aria-hidden="true" />
              </button>
            </div>

            <!-- Progress Bar (visual countdown) -->
            <div
              v-if="toast.timeout > 0"
              class="mt-3 h-1 bg-white/30 rounded-full overflow-hidden"
            >
              <div
                :class="['h-full transition-all ease-linear', toast.colorClasses.progress]"
                :style="{
                  width: '100%',
                  animation: `shrink ${toast.timeout}ms linear`
                }"
              ></div>
            </div>
          </div>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script setup>
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon
} from '@heroicons/vue/24/outline'
import { useToast } from '@/composables/useToast'

// Get toasts from composable
const { toasts, removeToast } = useToast()

/**
 * Get icon component by name
 */
const getIconComponent = (iconName) => {
  const iconComponents = {
    CheckCircleIcon,
    XCircleIcon,
    ExclamationTriangleIcon,
    InformationCircleIcon
  }
  return iconComponents[iconName] || InformationCircleIcon
}
</script>

<style scoped>
@reference "../../assets/styles/main.css";

/* Toast enter/leave transitions */
.toast-enter-active {
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.toast-leave-active {
  transition: all 0.2s ease-out;
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(100px) scale(0.95);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(100px) scale(0.9);
}

.toast-move {
  transition: transform 0.3s ease;
}

/* Progress bar shrink animation */
@keyframes shrink {
  from {
    width: 100%;
  }
  to {
    width: 0%;
  }
}

/* Mobile responsiveness */
@media (max-width: 640px) {
  .fixed.top-20.right-4 {
    @apply right-2 left-2 top-16;
  }
}
</style>
